var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import customEventHandler from '../helper/customEvents';
import { playAudio } from '../helper/audioHelper';
import { announce } from '../helper/announcer';
import { useCreationAnnounce, useDuplicateAnnounce, } from '../helper/hooks/useDuplicateAnnounce';
import { selectTool } from '../helper/helper';
import { MARKER_ASSET_BY_ID, GAME_MARKER_DEFAULT_COLOR, GAME_MARKER_PALETTE, } from '../constants/coinsAndCountersConstants';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
import '../styles/widgets/coinsAndCounters.scss';
var ASSET_BASE = './assets/images/manipulative/coinsAndCounters';
// Coins + bean — types with distinct front/back images. `hasTwoSides()` is
// driven by membership in this map, which decides whether the popup's Flip
// pill becomes available and whether random-flip-on-click runs.
//
// Bean has its own renderer (BeanShape, see below) that recolors the SVG body
// from the user-picked palette color, so it doesn't appear here. We still
// register it in the lookup so `hasTwoSides('bean')` returns true and the
// flip behavior is identical to coins.
var TWO_SIDED_ASSETS = {
    penny: { front: "".concat(ASSET_BASE, "/pennyHeads.svg"), back: "".concat(ASSET_BASE, "/pennyTails.svg") },
    nickel: { front: "".concat(ASSET_BASE, "/nickelHeads.svg"), back: "".concat(ASSET_BASE, "/nickelTails.svg") },
    dime: { front: "".concat(ASSET_BASE, "/dimeHeads.svg"), back: "".concat(ASSET_BASE, "/dimeTails.svg") },
    quarter: { front: "".concat(ASSET_BASE, "/quarterHeads.svg"), back: "".concat(ASSET_BASE, "/quarterTails.svg") },
    halfDollar: { front: "".concat(ASSET_BASE, "/halfDollarHeads.svg"), back: "".concat(ASSET_BASE, "/halfDollarTails.svg") },
    // bean is rendered via BeanShape — see below.
};
// Default bean colors — these match the original red/white from the
// graphics-team source files, but each instance can override them via
// `data.color` (front) and `data.backColor` (back).
export var BEAN_DEFAULT_FRONT_COLOR = '#f72a31';
export var BEAN_DEFAULT_BACK_COLOR = '#ffffff';
// Module-level cache: load the bean SVG once and reuse the template forever.
// We strip the SVG's own width/height so the parent's CSS controls sizing.
var beanSvgTemplate = null;
var beanSvgPromise = null;
var BEAN_BODY_FILL = '#f72a31';
var loadBeanSvg = function () {
    if (beanSvgTemplate)
        return Promise.resolve(beanSvgTemplate);
    if (!beanSvgPromise) {
        beanSvgPromise = fetch("".concat(ASSET_BASE, "/beanRed.svg"))
            .then(function (r) { return r.text(); })
            .then(function (text) {
            // Strip width/height from the root <svg> tag so wrapper CSS controls
            // the rendered size.
            var stripped = text.replace(/<svg([^>]*)>/, function (_, attrs) {
                return '<svg' + attrs.replace(/\s*(?:width|height)="[^"]*"/g, '') + ' preserveAspectRatio="xMidYMid meet">';
            });
            beanSvgTemplate = stripped;
            return stripped;
        });
    }
    return beanSvgPromise;
};
// Render the bean as an <img> backed by a data: URL of the recolored SVG.
// This makes the bean behave identically to the coin <img> elements for
// pointer events — the workspace wrapper's drag handlers receive mousedown
// directly, the same way they do for coins. Earlier inline-SVG approaches
// (dangerouslySetInnerHTML) leaked pointer events through the SVG's
// painted paths and broke drag-while-selected.
var BeanShape = function (_a) {
    var color = _a.color, width = _a.width, height = _a.height, transparent = _a.transparent;
    var _b = useState(beanSvgTemplate), template = _b[0], setTemplate = _b[1];
    useEffect(function () {
        if (template)
            return;
        var cancelled = false;
        loadBeanSvg().then(function (t) {
            if (!cancelled)
                setTemplate(t);
        });
        return function () {
            cancelled = true;
        };
    }, [template]);
    if (!template) {
        // Reserve the layout slot while the SVG template loads.
        return (_jsx("span", { className: "ccBeanShape", style: {
                display: 'inline-block',
                width: width,
                height: height,
                pointerEvents: 'none',
            } }));
    }
    var colored = template.replace(new RegExp("fill=\"".concat(BEAN_BODY_FILL, "\""), 'i'), "fill=\"".concat(color, "\""));
    var dataUrl = "data:image/svg+xml,".concat(encodeURIComponent(colored));
    return (_jsx("img", { src: dataUrl, alt: "bean", width: width, height: height, draggable: false, className: "ccBeanShape", style: {
            display: 'block',
            pointerEvents: 'none',
            opacity: transparent ? 0.45 : 1,
        } }));
};
// Single-image, single-sided counter types.
var SINGLE_SIDED_ASSETS = {
    x: "".concat(ASSET_BASE, "/cross.svg"),
    o: "".concat(ASSET_BASE, "/zero.svg"),
    stick: "".concat(ASSET_BASE, "/stick.svg"),
    bundle10: "".concat(ASSET_BASE, "/bundle10.svg"),
    bundle100: "".concat(ASSET_BASE, "/bundle100.svg"),
    hundredFrame: "".concat(ASSET_BASE, "/hundredFrame.svg"),
};
var CoinsAndCountersManipulative = function (_a) {
    var _b, _c;
    var data = _a.data;
    var counterType = data.counterType, center = data.center, dimensions = data.dimensions, _d = data.scale, scale = _d === void 0 ? 1 : _d, _e = data.rotation, rotation = _e === void 0 ? 0 : _e, lock = data.lock, id = data.id, _f = data.flipEnabled, flipEnabled = _f === void 0 ? false : _f, _g = data.currentSide, currentSide = _g === void 0 ? 'front' : _g, color = data.color, backColor = data.backColor, transparent = data.transparent, groupId = data.groupId;
    var _h = useContext(DataContext), manipulative = _h.manipulative, setManipulative = _h.setManipulative, selectedTool = _h.selectedTool, setSelectedTool = _h.setSelectedTool, panningEnable = _h.panningEnable, setActiveManipulator = _h.setActiveManipulator, setManiplulatorComponentVisible = _h.setManiplulatorComponentVisible, setSelectedManipulativeId = _h.setSelectedManipulativeId, contextMenu = _h.contextMenu, setContextMenu = _h.setContextMenu;
    var zoomLevel = globalStore.useStoreState(function (s) { return s.playerStore; }).zoomLevel;
    // Broadcasts this coin's drag as a multi-select leader so OTHER-type items
    // (dice, tiles, decks, ...) selected alongside it also follow. This widget's
    // own `movables` system below (built in onPointerDown) already coordinates
    // OTHER coinsAndCounters items directly — it predates and doesn't use this
    // hook — so coinsAndCounters is excluded from the central engine's captured
    // followers (see SELF_MANAGED_TYPES in multiDragHelper.ts) to avoid a
    // double-move; the follower effect further below covers coinsAndCounters
    // following a DIFFERENT-type leader instead.
    var _j = useMultiSelectDragSource(id, selectedTool), startMultiDrag = _j.startMultiDrag, moveMultiDrag = _j.moveMultiDrag, endMultiDrag = _j.endMultiDrag;
    var centerRef = useRef(center);
    centerRef.current = center;
    var selectedToolRef = useRef(selectedTool);
    selectedToolRef.current = selectedTool;
    var _k = useState(false), isDragging = _k[0], setIsDragging = _k[1];
    // Flip animation. The rendered side lags `data.currentSide` by 200ms so
    // the swap lands at the visual midpoint (scaleX(0) — coin edge-on). The
    // animation is triggered explicitly from the click handler / group-flip
    // event, NOT implicitly from a `currentSide` change — otherwise a random
    // flip that lands on the same side wouldn't animate (matches dice, which
    // always spins on click regardless of result).
    var _l = useState(currentSide), renderedSide = _l[0], setRenderedSide = _l[1];
    var _m = useState(false), isFlipping = _m[0], setIsFlipping = _m[1];
    var flipTimeoutsRef = useRef({});
    var runFlipAnimation = function (newSide) {
        // Cancel any in-progress animation so a rapid second click doesn't
        // leave the wrapper visually stuck in scaleX(0).
        if (flipTimeoutsRef.current.swap)
            clearTimeout(flipTimeoutsRef.current.swap);
        if (flipTimeoutsRef.current.end)
            clearTimeout(flipTimeoutsRef.current.end);
        setIsFlipping(false);
        // Re-trigger the keyframe in the next frame (browsers ignore animation
        // changes that happen in the same tick after stripping the class).
        requestAnimationFrame(function () {
            setIsFlipping(true);
            // Total = 10 cycles × 197ms = 1970ms (synced to flipCoinsAndCounter.mp3 ~1968ms).
            // Swap at the midpoint of cycle 9 (25 + 8×197 = 1601ms — a scaleX(0) instant)
            // so the result lands at the end of the spin, like dice.
            flipTimeoutsRef.current.swap = window.setTimeout(function () { return setRenderedSide(newSide); }, 1601);
            flipTimeoutsRef.current.end = window.setTimeout(function () { return setIsFlipping(false); }, 1970);
        });
    };
    // Listen for a group-flip animation request from CoinsAndCountersGroupOverlay.
    useEffect(function () {
        var onFlip = function (evt) {
            if (evt.id !== id)
                return;
            runFlipAnimation(evt.newSide);
        };
        customEventHandler.addEventListener('coinsAndCountersFlipAnimate', onFlip);
        return function () {
            return customEventHandler.removeEventListener('coinsAndCountersFlipAnimate', onFlip);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);
    var wrapperRef = useRef(null);
    // dragStartRef holds the cursor origin and a list of "movables" — usually
    // just this counter, or every member when the user grabs a coin that's part
    // of an already-selected group (group drag = move all members together).
    var dragStartRef = useRef(null);
    // Follow a DIFFERENT-type leader (e.g. Dice, a card deck) dragging alongside this coin in
    // the current multi-select. Mirrors ColoredTiles' generic follower pattern. Following ANOTHER
    // coinsAndCounters item is still handled by this file's own `movables` system in
    // onPointerDown/onPointerMove — this effect only needs to fire for a source of some other type.
    useEffect(function () {
        var isMultiSelected = selectedTool.includes(id) && selectedTool.length > 1;
        if (isMultiSelected === false)
            return;
        var myStart = { x: centerRef.current.x, y: centerRef.current.y };
        var followerOldCenter = null;
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === id)
                return;
            myStart = { x: centerRef.current.x, y: centerRef.current.y };
            followerOldCenter = __assign({}, myStart);
        };
        var onMove = function (_a) {
            var sourceId = _a.sourceId, deltaX = _a.deltaX, deltaY = _a.deltaY;
            if (sourceId === id || (lock === null || lock === void 0 ? void 0 : lock.positioning))
                return;
            var inMultiSel = selectedToolRef.current.includes(id) && selectedToolRef.current.length > 1;
            if (!inMultiSel)
                return;
            var el = wrapperRef.current;
            if (!el)
                return;
            el.style.left = "".concat(myStart.x + deltaX, "px");
            el.style.top = "".concat(myStart.y + deltaY, "px");
        };
        var onEnd = function (_a) {
            var _b;
            var sourceId = _a.sourceId;
            if (sourceId === id || !followerOldCenter)
                return;
            var el = wrapperRef.current;
            var newCenter = el
                ? {
                    x: parseFloat(el.style.left) || followerOldCenter.x,
                    y: parseFloat(el.style.top) || followerOldCenter.y,
                }
                : followerOldCenter;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter }) }) : item;
                });
            });
            var entry = {
                type: id,
                id: (_b = id.split('_')[1]) !== null && _b !== void 0 ? _b : id,
                propertyName: 'center',
                old: JSON.stringify(followerOldCenter),
                new: JSON.stringify(newCenter),
            };
            if (!registerMultiSelectSaveEntries(sourceId, [entry])) {
                saveState(entry);
            }
            followerOldCenter = null;
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        customEventHandler.addEventListener('multiSelectMoveEnd', onEnd);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
            customEventHandler.removeEventListener('multiSelectMoveEnd', onEnd);
        };
    }, [selectedTool, id, lock === null || lock === void 0 ? void 0 : lock.positioning, setManipulative]);
    // Keyboard movement bookkeeping (Dice parity). startRef captures the
    // pre-keypress center once per arrow-key burst; curPosRef holds the live
    // position so successive arrow ticks compound. saveTimer debounces the
    // undo entry so holding the arrow key produces ONE undo step, not 50.
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    var keyMoveSaveTimer = useRef(null);
    // iPad ghost-click guard — see CoinsAndCountersToolbar for the full
    // explanation. iOS Safari fires synthetic mousedown ~300 ms after
    // touchend on tappable elements; without this guard, a single tap on
    // a flippable coin would fire onPointerDown twice and flip the coin
    // twice in a row.
    var lastTouchAtRef = useRef(0);
    var longPressRef = useRef(null);
    // Tracks whether the pointer-down → pointer-up sequence was a drag so the
    // onClick handler (which owns the flip trigger) can skip flipping after a drag.
    var didDragRef = useRef(false);
    var isDraggable = !(lock === null || lock === void 0 ? void 0 : lock.positioning) && !(lock === null || lock === void 0 ? void 0 : lock.editing) && !panningEnable;
    // Per-counter friendly name — reused by the announce hooks so screen
    // readers hear "Penny added" rather than the generic "Coins & Counters
    // added". Kept here (not inside buildAriaLabel) so the t() call is
    // resolved at render time when the hooks read it.
    var friendlyName = t("text.coinsAndCountersTypeAria_".concat(counterType));
    // Temporary aria-label overrides — each hook returns a non-null string
    // for ~500ms after the corresponding event (drop / right-click duplicate)
    // so the screen reader announces the action instead of the static label.
    // After 500ms the override clears and buildAriaLabel() resumes.
    var creationLabel = useCreationAnnounce(id, friendlyName);
    var duplicateLabel = useDuplicateAnnounce(id, friendlyName);
    var isSelected = selectedTool.includes(id);
    // True when this coin's group is currently selected — clicking the coin
    // should grab the GROUP (drag all members) rather than the individual coin.
    var isInSelectedGroup = !!groupId && selectedTool[0] === groupId;
    // True when this coin is part of a pre-group multi-select (lasso / shift-
    // click selected 2+ counters but the user hasn't pressed Group yet). Drag
    // should move every selected coin together, like Pattern Shapes.
    var isInMultiSelect = selectedTool.length > 1 && selectedTool.includes(id);
    var onPointerDown = function (e) {
        // Right-click is reserved for the context menu — `onContextMenu` opens
        // the menu, and we don't want the same click to also flip the coin.
        // (Browsers fire `mousedown` with button=2 BEFORE `contextmenu`, so
        // without this guard we'd capture dragStartRef and onPointerUp's
        // click-no-drag branch would fire flipCards.)
        if ('button' in e && e.button === 2)
            return;
        // iPad ghost-click guard — ignore synthetic mousedown that follows
        // a real touchstart by ~300 ms.
        var isTouch = 'touches' in e;
        if (isTouch) {
            lastTouchAtRef.current = Date.now();
        }
        else if (Date.now() - lastTouchAtRef.current < 500) {
            return;
        }
        // ── Selection ─────────────────────────────────────────────────────────
        // 1. Coin already part of an active group/multi-select → keep selection,
        //    drag all members together (Pattern-Shapes parity).
        // 2. Coin in a group (group not yet selected) → select the group.
        // 3. Multi-select modifier (shift/ctrl/cmd) → toggle this coin via
        //    the shared `selectTool` helper.
        // 4. Plain click → single-select this coin.
        // Selection updates run BEFORE the lock check so a locked counter can
        // still be re-selected to unlock.
        var modifierHeld = ('ctrlKey' in e && e.ctrlKey) ||
            ('metaKey' in e && e.metaKey) ||
            ('shiftKey' in e && e.shiftKey);
        if (isInSelectedGroup || (isInMultiSelect && !modifierHeld)) {
            // Don't change selection — the active group/multi-select stays put.
        }
        else if (groupId) {
            setSelectedTool([groupId]);
        }
        else {
            var fakeEvt = {
                ctrlKey: 'ctrlKey' in e ? e.ctrlKey : false,
                metaKey: 'metaKey' in e ? e.metaKey : false,
                shiftKey: 'shiftKey' in e ? e.shiftKey : false,
            };
            var next = selectTool(fakeEvt, id, selectedTool);
            setSelectedTool(next.length > 0 ? next : [id]);
        }
        var point = 'touches' in e ? e.touches[0] : e;
        // Build the list of items that will move together with this drag:
        //   coin in a formal group → every group member (regardless of whether
        //     the group is the *current* selection — the click both selects
        //     the group AND drags it in one motion, like Pattern Shapes)
        //   multi-select          → every selected coin
        //   else                  → just this coin
        // (When the group/multi-select is active, the CoinsAndCountersGroupOverlay
        //  intercepts the click before this handler fires; this branch is the
        //  fallback for the very first click on a grouped coin.)
        // For LOCKED counters we still go through the rest of this handler —
        // dragStartRef is needed so onPointerUp's click-no-drag branch can fire
        // a flip (per spec: "Locking doesn't keep a counter from being flipped").
        // onPointerMove then gates the actual movement tracking on `isDraggable`.
        var movables = [];
        if (isDraggable) {
            if (groupId) {
                manipulative.forEach(function (m) {
                    var _a;
                    if (((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'coinsAndCounters' &&
                        m.data.groupId === groupId &&
                        m.data.id) {
                        movables.push({
                            id: m.data.id,
                            startCenter: __assign({}, m.data.center),
                            el: document.querySelector("[data-id=\"".concat(m.data.id, "\"]")),
                        });
                    }
                });
            }
            else if (isInMultiSelect && !modifierHeld) {
                var selSet_1 = new Set(selectedTool);
                manipulative.forEach(function (m) {
                    var _a, _b;
                    if (((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'coinsAndCounters' &&
                        selSet_1.has((_b = m.data.id) !== null && _b !== void 0 ? _b : '') &&
                        m.data.id) {
                        movables.push({
                            id: m.data.id,
                            startCenter: __assign({}, m.data.center),
                            el: document.querySelector("[data-id=\"".concat(m.data.id, "\"]")),
                        });
                    }
                });
            }
            else {
                movables.push({ id: id, startCenter: __assign({}, center), el: wrapperRef.current });
            }
        }
        didDragRef.current = false;
        dragStartRef.current = {
            clientX: point.clientX,
            clientY: point.clientY,
            hasMoved: false,
            movables: movables,
            overlayEl: null,
            overlayStartLeft: 0,
            overlayStartTop: 0,
        };
        window.addEventListener('mousemove', onPointerMove);
        window.addEventListener('touchmove', onPointerMove, { passive: false });
        window.addEventListener('mouseup', onPointerUp);
        window.addEventListener('touchend', onPointerUp);
        startMultiDrag();
    };
    // On iPad, iOS Safari does not fire onContextMenu on long-press.
    // This handler starts a 600ms timer to open the context menu on touch;
    // the timer is cancelled if the user moves (drag) or lifts early.
    var handleTouchStart = function (e) {
        var clientX = e.touches[0].clientX;
        var clientY = e.touches[0].clientY;
        longPressRef.current = setTimeout(function () {
            longPressRef.current = null;
            setIsDragging(false);
            dragStartRef.current = null;
            var target = groupId !== null && groupId !== void 0 ? groupId : id;
            if (!selectedTool.includes(target))
                setSelectedTool([target]);
            setContextMenu({ x: clientX, y: clientY, manipulativeId: target });
        }, 600);
        onPointerDown(e);
    };
    var onPointerMove = function (e) {
        var start = dragStartRef.current;
        if (!start)
            return;
        if (longPressRef.current) {
            clearTimeout(longPressRef.current);
            longPressRef.current = null;
        }
        // Locked counters: skip ALL movement tracking. dragStartRef stays
        // captured so onPointerUp's click-no-drag flip branch still fires.
        if (!isDraggable)
            return;
        var point = 'touches' in e ? e.touches[0] : e;
        var dx = (point.clientX - start.clientX) / zoomLevel;
        var dy = (point.clientY - start.clientY) / zoomLevel;
        if (!start.hasMoved && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
            start.hasMoved = true;
            didDragRef.current = true;
            setIsDragging(true);
            // Lazily capture the group overlay element the first time the drag
            // threshold is crossed. The overlay may have just mounted (React
            // batches the setSelectedTool([groupId]) call from onPointerDown),
            // so it's not available at dragStartRef initialisation time.
            if (groupId && !start.overlayEl) {
                var el = document.querySelector(".ccGroupOutline[data-group-id=\"".concat(groupId, "\"]"));
                if (el) {
                    start.overlayEl = el;
                    start.overlayStartLeft = el.offsetLeft;
                    start.overlayStartTop = el.offsetTop;
                }
            }
        }
        if (start.hasMoved) {
            // Direct DOM mutation per member — avoids re-rendering all coins on
            // every mouse-move tick. Final commit happens once on pointer-up.
            start.movables.forEach(function (mv) {
                if (mv.el) {
                    mv.el.style.left = "".concat(mv.startCenter.x + dx, "px");
                    mv.el.style.top = "".concat(mv.startCenter.y + dy, "px");
                }
            });
            // Keep the group outline in sync with the moving members.
            if (start.overlayEl) {
                start.overlayEl.style.left = "".concat(start.overlayStartLeft + dx, "px");
                start.overlayEl.style.top = "".concat(start.overlayStartTop + dy, "px");
            }
            // Force ContextMenu to re-render so it re-reads getBoundingClientRect()
            // on the now-moved wrapper and repositions itself. Mirrors how
            // DiceManipulative achieves this via setDicePosition on every drag frame.
            if (contextMenu)
                setContextMenu(function (prev) { return prev ? __assign({}, prev) : null; });
            // Broadcast so any OTHER-type item in the current multi-select follows
            // this coin's drag (see the hook wiring above for why coinsAndCounters
            // followers are handled separately, by this file's own movables loop).
            moveMultiDrag(dx, dy);
        }
        if ('touches' in e)
            e.preventDefault();
    };
    var onPointerUp = function (e) {
        var start = dragStartRef.current;
        if (longPressRef.current) {
            clearTimeout(longPressRef.current);
            longPressRef.current = null;
        }
        window.removeEventListener('mousemove', onPointerMove);
        window.removeEventListener('touchmove', onPointerMove);
        window.removeEventListener('mouseup', onPointerUp);
        window.removeEventListener('touchend', onPointerUp);
        if (!start)
            return;
        if (start.hasMoved) {
            // ---- Drag commit ----
            var point = 'changedTouches' in e
                ? e.changedTouches[0]
                : e;
            var dx_1 = (point.clientX - start.clientX) / zoomLevel;
            var dy_1 = (point.clientY - start.clientY) / zoomLevel;
            // One undo entry per moved coin keeps the existing single-coin
            // 'center' undo path working unchanged. (Group ungroup-undo is
            // already a separate completeData entry from GameController.)
            var updates = start.movables.map(function (mv) { return ({
                id: mv.id,
                oldCenter: mv.startCenter,
                newCenter: {
                    x: mv.startCenter.x + dx_1,
                    y: mv.startCenter.y + dy_1,
                },
            }); });
            // One batched array (not a saveState call per item) so the whole group's drag — and any
            // OTHER-type item also selected alongside it — undoes/redoes together in a single step.
            var centerEntries = updates.map(function (u) {
                var _a;
                return ({
                    type: u.id,
                    id: (_a = u.id.split('_')[1]) !== null && _a !== void 0 ? _a : u.id,
                    propertyName: 'center',
                    old: JSON.stringify(u.oldCenter),
                    new: JSON.stringify(u.newCenter),
                });
            });
            if (!registerMultiSelectSaveEntries(id, centerEntries)) {
                saveState(centerEntries);
            }
            var updateMap_1 = new Map(updates.map(function (u) { return [u.id, u.newCenter]; }));
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var _a;
                    return updateMap_1.has((_a = item.data) === null || _a === void 0 ? void 0 : _a.id)
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: updateMap_1.get(item.data.id) }) }) : item;
                });
            });
            endMultiDrag();
        }
        // Flip is now triggered in onClick (see below) so it fires only on the
        // VO-focused element's synthetic click, not on whatever coin the physical
        // double-tap coordinates happen to land on.
        dragStartRef.current = null;
        setIsDragging(false);
    };
    // Cleanup global listeners on unmount.
    useEffect(function () {
        return function () {
            window.removeEventListener('mousemove', onPointerMove);
            window.removeEventListener('touchmove', onPointerMove);
            window.removeEventListener('mouseup', onPointerUp);
            window.removeEventListener('touchend', onPointerUp);
            if (keyMoveSaveTimer.current)
                clearTimeout(keyMoveSaveTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Random flip + state commit + animation + announcement.
    // Shared by mouse-click, right-click menu, and keyboard (Enter/Space).
    var triggerRandomFlip = function () {
        var _a;
        if (!flipEnabled)
            return;
        var nextSide = Math.random() < 0.5 ? 'front' : 'back';
        runFlipAnimation(nextSide);
        saveState({
            type: id,
            id: (_a = id.split('_')[1]) !== null && _a !== void 0 ? _a : id,
            propertyName: 'currentSide',
            old: JSON.stringify(currentSide),
            new: JSON.stringify(nextSide),
        });
        setManipulative(function (prev) {
            return prev.map(function (item) {
                var _a;
                return ((_a = item.data) === null || _a === void 0 ? void 0 : _a.id) === id
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { currentSide: nextSide }) }) : item;
            });
        });
        playAudio('flipCoinsAndCounter');
        // Announce the new side after the flip animation ends (1970ms).
        // Coins: "Heads" / "Tails". Game markers / beans: color name of the
        // new side (e.g. "Black" for a game marker whose back is black).
        window.setTimeout(function () {
            var isCoinFace = counterType === 'penny' ||
                counterType === 'nickel' ||
                counterType === 'dime' ||
                counterType === 'quarter' ||
                counterType === 'halfDollar';
            if (isCoinFace) {
                announce(t(nextSide === 'front' ? 'text.heads' : 'text.tails'));
            }
            else if (counterType === 'gameMarker') {
                var newColor = nextSide === 'front' ? color : backColor;
                var colorName = newColor && MARKER_COLOR_KEY[newColor]
                    ? t(MARKER_COLOR_KEY[newColor])
                    : t(nextSide === 'front' ? 'text.front' : 'text.back');
                announce(colorName);
            }
            else if (counterType === 'bean') {
                var newHex_1 = nextSide === 'front' ? color : backColor;
                var beanEntry = newHex_1 ? GAME_MARKER_PALETTE.find(function (s) { return s.hex === newHex_1; }) : undefined;
                var colorName = beanEntry
                    ? t(beanEntry.colorLabelKey)
                    : t(nextSide === 'front' ? 'text.front' : 'text.back');
                announce(colorName);
            }
            else {
                announce(t(nextSide === 'front' ? 'text.front' : 'text.back'));
            }
        }, 500);
    };
    var onKeyDown = function (e) {
        var _a, _b;
        // Ignore keys mid-animation so a second Enter while still spinning
        // doesn't queue a stuttery flip-on-flip. Matches Dice's `if (animate) return`.
        if (isFlipping)
            return;
        // Context menu — Enter, Space, Shift+F10, ContextMenu key, or F10.
        // Flip is available as a menu item inside the context menu.
        if (e.key === 'Enter' ||
            e.key === ' ' ||
            e.key === 'ContextMenu' ||
            (e.shiftKey && e.key === 'F10') ||
            e.key === 'F10') {
            e.preventDefault();
            var target = groupId !== null && groupId !== void 0 ? groupId : id;
            if (!selectedTool.includes(target))
                setSelectedTool([target]);
            var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect) {
                setContextMenu({
                    x: rect.right,
                    y: rect.top,
                    manipulativeId: target,
                });
            }
            return;
        }
        // Arrow movement — only when actually draggable. We swallow the key
        // either way so the stage doesn't scroll behind the focused counter.
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
            e.preventDefault();
            if (!isDraggable)
                return;
            var curPos = (_b = keyMoveCurPosRef.current) !== null && _b !== void 0 ? _b : center;
            if (!keyMoveStartRef.current) {
                keyMoveStartRef.current = __assign({}, curPos);
            }
            var step = e.shiftKey ? 50 : 10;
            var scaledStep = step / zoomLevel;
            var delta = {
                ArrowUp: { x: 0, y: -scaledStep },
                ArrowDown: { x: 0, y: scaledStep },
                ArrowLeft: { x: -scaledStep, y: 0 },
                ArrowRight: { x: scaledStep, y: 0 },
            };
            var d = delta[e.key];
            var newCenter_1 = { x: curPos.x + d.x, y: curPos.y + d.y };
            keyMoveCurPosRef.current = newCenter_1;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    var _a;
                    return ((_a = item.data) === null || _a === void 0 ? void 0 : _a.id) === id
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter_1 }) }) : item;
                });
            });
            // Debounce both the undo save and the announcement so a held
            // arrow key produces one undo entry and one final readout.
            if (keyMoveSaveTimer.current)
                clearTimeout(keyMoveSaveTimer.current);
            keyMoveSaveTimer.current = setTimeout(function () {
                var _a;
                var oldPos = keyMoveStartRef.current;
                var newPos = keyMoveCurPosRef.current;
                if (!oldPos || !newPos)
                    return;
                saveState({
                    type: id,
                    id: (_a = id.split('_')[1]) !== null && _a !== void 0 ? _a : id,
                    propertyName: 'center',
                    old: JSON.stringify(oldPos),
                    new: JSON.stringify(newPos),
                });
                var dx = Math.round(newPos.x - oldPos.x);
                var dy = Math.round(newPos.y - oldPos.y);
                var directions = [];
                if (dy < 0)
                    directions.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
                if (dy > 0)
                    directions.push("".concat(t('text.down'), " ").concat(dy));
                if (dx < 0)
                    directions.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
                if (dx > 0)
                    directions.push("".concat(t('text.right'), " ").concat(dx));
                if (directions.length > 0) {
                    announce(t('text.manipulativeMoved', {
                        name: t('text.coinsAndCounters'),
                        direction: directions.join(" ".concat(t('text.and'), " ")),
                        x: Math.round(newPos.x),
                        y: Math.round(newPos.y),
                    }));
                }
                keyMoveStartRef.current = null;
                keyMoveCurPosRef.current = null;
            }, 500);
        }
    };
    // Bottom-panel scale buttons (the floating [- 1.0 +] widget that shows
    // when this counter is the active selection). The BottomPanel dispatches
    // one event per click; every placed counter receives it, so we gate on
    // whether this instance's id is the selected tool before mutating.
    useEffect(function () {
        if (!isSelected && !isInSelectedGroup)
            return;
        var onToolClick = function (evt) {
            var _a;
            // Configure / Edit click — reopen the configuration popup pre-populated
            // with the user's current drawer entries (CoinsAndCountersManipulativePopup
            // reads `coinsAndCountersDrawer` on mount and rehydrates its state).
            if (evt.id === 'edit') {
                // Only one coin in a group should trigger the popup (the first one
                // that handles it wins; others ignore it via isSelected guard).
                if (!isSelected)
                    return;
                setActiveManipulator('coinsAndCounters');
                setManiplulatorComponentVisible(true);
                setSelectedManipulativeId(id);
                return;
            }
            // Flip from the right-click context menu — same dice-style random
            // flip as a click on the coin (the helper guards on flipEnabled and
            // also emits the screen-reader announcement).
            if (evt.id === 'flip') {
                triggerRandomFlip();
                return;
            }
            if (evt.id !== 'scalePlus' && evt.id !== 'scaleMinus')
                return;
            // When in a group, only the member with the lowest groupPosition handles
            // the scale event — prevents every member firing and multiplying the step.
            if (isInSelectedGroup) {
                var groupMembers = manipulative.filter(function (m) { return m.data.groupId === groupId; });
                var sorted = __spreadArray([], groupMembers, true).sort(function (a, b) {
                    var _a, _b;
                    return ((_a = a.data.groupPosition) !== null && _a !== void 0 ? _a : 0) -
                        ((_b = b.data.groupPosition) !== null && _b !== void 0 ? _b : 0);
                });
                if (((_a = sorted[0]) === null || _a === void 0 ? void 0 : _a.data.id) !== id)
                    return;
            }
            var isPlus = evt.id === 'scalePlus';
            var _oldScale;
            var _newScale;
            setManipulative(function (prev) {
                var _a;
                var next = prev.map(function (item) {
                    var _a, _b, _c;
                    // Scale this piece when it's directly selected, OR when it belongs
                    // to the currently selected group (group scale = scale all members).
                    var isThisPiece = ((_a = item.data) === null || _a === void 0 ? void 0 : _a.id) === id;
                    var isGroupMember = isInSelectedGroup &&
                        ((_b = item.data) === null || _b === void 0 ? void 0 : _b.type) === 'coinsAndCounters' &&
                        item.data.groupId === groupId;
                    if (!isThisPiece && !isGroupMember)
                        return item;
                    var oldScale = (_c = item.data.scale) !== null && _c !== void 0 ? _c : 1;
                    var newScale = isPlus
                        ? Math.min(5, parseFloat((oldScale + 0.1).toFixed(1)))
                        : Math.max(0.5, parseFloat((oldScale - 0.1).toFixed(1)));
                    if (isThisPiece) {
                        _oldScale = oldScale;
                        _newScale = newScale;
                    }
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: newScale }) });
                });
                if (_oldScale !== undefined &&
                    _newScale !== undefined &&
                    _oldScale !== _newScale) {
                    saveState({
                        type: id,
                        id: (_a = id.split('_')[1]) !== null && _a !== void 0 ? _a : id,
                        propertyName: 'scale',
                        old: JSON.stringify(_oldScale),
                        new: JSON.stringify(_newScale),
                    });
                }
                return next;
            });
        };
        customEventHandler.addEventListener('bottomPanel', onToolClick);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
        };
    }, [isSelected, isInSelectedGroup, groupId, id, setManipulative, setActiveManipulator, setManiplulatorComponentVisible, setSelectedManipulativeId]);
    var width = dimensions.width;
    var height = dimensions.height;
    // Instance number: position among all canvas coins with the same type+color.
    // Groups: game marker by color id, bean by front-color hex, all others by type only.
    var instanceNumber = useMemo(function () {
        var instanceKey = counterType === 'gameMarker' ? "gameMarker_".concat(color !== null && color !== void 0 ? color : '') :
            counterType === 'bean' ? "bean_".concat(color !== null && color !== void 0 ? color : '') :
                counterType;
        var peers = manipulative.filter(function (m) {
            var _a, _b;
            if (m.data.type !== 'coinsAndCounters')
                return false;
            var d = m.data;
            var key = d.counterType === 'gameMarker' ? "gameMarker_".concat((_a = d.color) !== null && _a !== void 0 ? _a : '') :
                d.counterType === 'bean' ? "bean_".concat((_b = d.color) !== null && _b !== void 0 ? _b : '') :
                    d.counterType;
            return key === instanceKey;
        });
        return peers.findIndex(function (m) { return m.data.id === id; }) + 1;
    }, [manipulative, id, counterType, color]);
    // Map game-marker color id → existing translation key. The palette uses
    // 'grey' (British) but the existing app key is 'colorGray' — bridge here
    // rather than rename the palette id.
    var MARKER_COLOR_KEY = {
        red: 'text.colorRed',
        yellow: 'text.colorYellow',
        blue: 'text.colorBlue',
        brown: 'text.colorBrown',
        orange: 'text.colorOrange',
        green: 'text.colorGreen',
        purple: 'text.colorPurple',
        black: 'text.colorBlack',
        white: 'text.colorWhite',
        grey: 'text.colorGray',
    };
    // Compose the screen-reader description. Order: identity → state → hints,
    // matching how Dice / NumberFrames structure their labels. Position is
    // intentionally NOT here — arrow-key moves announce position via the
    // live region, and including it in aria-label would cause a duplicate
    // re-read on every drag/keyboard tick.
    var buildAriaLabel = function () {
        var parts = [];
        // Type
        parts.push(t("text.coinsAndCountersTypeAria_".concat(counterType)));
        // Color — use the currently visible side's color, not always the front color.
        if (counterType === 'gameMarker') {
            var displayedColor = currentSide === 'back' ? (backColor !== null && backColor !== void 0 ? backColor : color) : color;
            if (displayedColor && MARKER_COLOR_KEY[displayedColor]) {
                parts.push(t(MARKER_COLOR_KEY[displayedColor]));
            }
        }
        else if (counterType === 'bean') {
            var displayedHex_1 = currentSide === 'back' ? (backColor !== null && backColor !== void 0 ? backColor : color) : color;
            if (displayedHex_1) {
                var beanColorEntry = GAME_MARKER_PALETTE.find(function (s) { return s.hex === displayedHex_1; });
                if (beanColorEntry)
                    parts.push(t(beanColorEntry.colorLabelKey));
            }
        }
        // Transparent modifier — applies to game markers and beans.
        if (transparent)
            parts.push(t('text.transparent'));
        // Instance number — distinguishes multiple coins of the same type+color.
        parts.push(String(instanceNumber));
        // Current side — coins read as heads/tails; everything else as front/back.
        if (flipEnabled) {
            var isCoinFace = counterType === 'penny' ||
                counterType === 'nickel' ||
                counterType === 'dime' ||
                counterType === 'quarter' ||
                counterType === 'halfDollar';
            parts.push(isCoinFace
                ? t(currentSide === 'front' ? 'text.heads' : 'text.tails')
                : t(currentSide === 'front' ? 'text.front' : 'text.back'));
        }
        // Selection / lock / group state. Group state is mostly redundant
        // (grouped coins are aria-hidden so they're not read out individually)
        // but kept for the rare case a screen reader navigates them anyway.
        if (isSelected)
            parts.push(t('text.selected'));
        if ((lock === null || lock === void 0 ? void 0 : lock.positioning) || (lock === null || lock === void 0 ? void 0 : lock.editing))
            parts.push(t('text.locked'));
        if (groupId)
            parts.push(t('text.inGroup'));
        // Action hints — only what's actually available right now. Locked
        // counter loses the move hint but keeps Flip per spec.
        parts.push(t('text.pressEnterForMenu'));
        if (isDraggable)
            parts.push(t('text.arrowKeysToMove'));
        return parts.join(', ');
    };
    return (_jsx("div", { ref: wrapperRef, "data-manipulative-id": id, className: "coinsAndCountersWrapper ".concat(isSelected ? 'selected' : '', " ").concat(isDragging ? 'dragging' : ''), style: {
            position: 'absolute',
            left: "".concat(center.x, "px"),
            top: "".concat(center.y, "px"),
            width: "".concat(width, "px"),
            height: "".concat(height, "px"),
            transform: "translate(-50%, -50%) scale(".concat(scale, ") rotate(").concat(rotation, "deg)"),
            cursor: isDraggable ? (isDragging ? 'grabbing' : 'grab') : 'default',
            userSelect: 'none',
            touchAction: 'none',
            zIndex: (_b = data.zIndex) !== null && _b !== void 0 ? _b : 11,
        }, onMouseDown: onPointerDown, onTouchStart: handleTouchStart, onKeyDown: onKeyDown, onClick: function (e) {
            var _a;
            if (e.detail === 0) {
                // VoiceOver on iPad fires a synthetic click (detail === 0) on double-tap
                // targeting the VO-focused element. Open the context menu here instead of
                // flipping — the physical touch coordinates may have landed on a different
                // coin, so flip must never run from the touch path (onPointerUp); it runs
                // here so only the correct (VO-focused) coin is affected.
                var target = groupId !== null && groupId !== void 0 ? groupId : id;
                if (!selectedTool.includes(target))
                    setSelectedTool([target]);
                var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                if (rect) {
                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: target });
                }
                return;
            }
            // Real click or touch tap (detail >= 1): flip if this was not a drag,
            // not a grouped coin (group flip handled by CoinsAndCountersGroupOverlay),
            // and not a multi-select grab.
            if (!didDragRef.current && !groupId && !isInMultiSelect) {
                triggerRandomFlip();
            }
        }, onContextMenu: function (e) {
            var _a;
            e.preventDefault();
            // Mirror Pattern Shapes: select the counter (or its group if in one)
            // before opening the menu so action items target the right item.
            var target = groupId !== null && groupId !== void 0 ? groupId : id;
            if (!selectedTool.includes(target))
                setSelectedTool([target]);
            var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            if (rect) {
                setContextMenu({
                    x: rect.right,
                    y: rect.top,
                    manipulativeId: target,
                });
            }
        }, 
        // When this counter is part of a formal manipulativeGroup_, the group
        // overlay (mounted by WhiteBoard) owns tab focus + keyboard; members
        // drop out of the tab order and are aria-hidden so screen readers
        // describe the group as a single object. Pre-group multi-select
        // (lasso / shift-click) does NOT mount the overlay, so members stay
        // tabbable in that case — Enter/arrows still work per-counter.
        tabIndex: groupId || panningEnable ? -1 : 0, "aria-hidden": groupId ? true : undefined, role: flipEnabled ? 'button' : 'img', "aria-label": (_c = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _c !== void 0 ? _c : buildAriaLabel(), "data-id": id, children: _jsx("div", { className: "ccFlipInner ".concat(isFlipping ? 'ccFlipping' : ''), children: _jsx(CounterGraphic, { counterType: counterType, width: width, height: height, side: renderedSide, color: color, backColor: backColor, transparent: transparent }) }) }));
};
// --------------------------------------------------------------------------
// CounterGraphic — coins use real SVG assets via <img>; non-coin types use
// inline placeholder shapes (replace with real assets in a follow-up pass).
// --------------------------------------------------------------------------
var CounterGraphic = function (_a) {
    var _b, _c;
    var counterType = _a.counterType, width = _a.width, height = _a.height, _d = _a.side, side = _d === void 0 ? 'front' : _d, color = _a.color, backColor = _a.backColor, transparent = _a.transparent;
    var baseImgStyle = {
        display: 'block',
        pointerEvents: 'none',
        opacity: transparent ? 0.45 : 1,
    };
    // Bean — recolorable inline SVG. Front/back colors come from data.
    if (counterType === 'bean') {
        var fill = side === 'back'
            ? backColor !== null && backColor !== void 0 ? backColor : BEAN_DEFAULT_BACK_COLOR
            : color !== null && color !== void 0 ? color : BEAN_DEFAULT_FRONT_COLOR;
        return (_jsx(BeanShape, { color: fill, width: width, height: height, transparent: transparent }));
    }
    // Coins — heads/tails image pair.
    var twoSided = TWO_SIDED_ASSETS[counterType];
    if (twoSided) {
        return (_jsx("img", { src: twoSided[side], alt: "".concat(counterType, " ").concat(side === 'front' ? 'heads' : 'tails'), width: width, height: height, draggable: false, style: baseImgStyle }));
    }
    // Game marker — pick the colored variant by `color` (front) or
    // `backColor` (back). When the back side has no variant, fall back to
    // the front variant so single-sided markers still render.
    if (counterType === 'gameMarker') {
        var variant = side === 'back'
            ? (_b = backColor !== null && backColor !== void 0 ? backColor : color) !== null && _b !== void 0 ? _b : GAME_MARKER_DEFAULT_COLOR
            : color !== null && color !== void 0 ? color : GAME_MARKER_DEFAULT_COLOR;
        var src = (_c = MARKER_ASSET_BY_ID[variant]) !== null && _c !== void 0 ? _c : MARKER_ASSET_BY_ID[GAME_MARKER_DEFAULT_COLOR];
        return (_jsx("img", { src: src, alt: "game marker ".concat(variant), width: width, height: height, draggable: false, style: baseImgStyle }));
    }
    // Single-image counter types (x, o, stick, bundle10, bundle100, hundredFrame).
    var single = SINGLE_SIDED_ASSETS[counterType];
    if (single) {
        return (_jsx("img", { src: single, alt: counterType, width: width, height: height, draggable: false, style: baseImgStyle }));
    }
    return null;
};
// True if the type has a heads/tails pair (i.e. flipping is meaningful).
// Bean isn't in TWO_SIDED_ASSETS but still needs Flip semantics. Game marker
// is also conceptually 2-sided per spec — both sides currently render the
// same image, but the flip toggle still gates the on-click flip behavior.
var hasTwoSides = function (counterType) {
    return Boolean(TWO_SIDED_ASSETS[counterType]) ||
        counterType === 'bean' ||
        counterType === 'gameMarker';
};
export default CoinsAndCountersManipulative;
export { CounterGraphic, BeanShape, hasTwoSides };
